import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useMutation } from "@apollo/client";
import { gql } from "@apollo/client";
import { ActivQMSLoader } from "../../components/ui/activqms-loader";
import { CheckCircleIcon, XCircleIcon } from "@heroicons/react/24/outline";

// GraphQL mutation for completing OAuth
const COMPLETE_SLACK_OAUTH = gql`
  mutation CompleteSlackOAuth($code: String!, $state: String!) {
    completeSlackOAuth(code: $code, state: $state) {
      success
      teamName
      channelName
      error
    }
  }
`;

interface OAuthResult {
  success: boolean;
  teamName?: string;
  channelName?: string;
  error?: string;
}

export function SlackOAuthCallback() {
  const [searchParams] = useSearchParams();
  const [result, setResult] = useState<OAuthResult | null>(null);
  const [isProcessing, setIsProcessing] = useState(true);

  const [completeOAuth] = useMutation(COMPLETE_SLACK_OAUTH, {
    onCompleted: (data) => {
      const oauthResult = data.completeSlackOAuth;
      setResult(oauthResult);
      setIsProcessing(false);

      // Send message to parent window
      if (window.opener) {
        if (oauthResult.success) {
          window.opener.postMessage(
            {
              type: "SLACK_OAUTH_SUCCESS",
              teamName: oauthResult.teamName,
              channelName: oauthResult.channelName,
            },
            window.location.origin
          );
        } else {
          window.opener.postMessage(
            {
              type: "SLACK_OAUTH_ERROR",
              error: oauthResult.error,
            },
            window.location.origin
          );
        }

        // Close popup after a short delay
        setTimeout(() => {
          window.close();
        }, 2000);
      }
    },
    onError: (error) => {
      console.error("OAuth completion error:", error);
      setResult({
        success: false,
        error: "Failed to complete OAuth process",
      });
      setIsProcessing(false);

      // Send error message to parent window
      if (window.opener) {
        window.opener.postMessage(
          {
            type: "SLACK_OAUTH_ERROR",
            error: error.message,
          },
          window.location.origin
        );

        // Close popup after a short delay
        setTimeout(() => {
          window.close();
        }, 2000);
      }
    },
  });

  useEffect(() => {
    const code = searchParams.get("code");
    const state = searchParams.get("state");
    const error = searchParams.get("error");

    if (error) {
      // OAuth was denied or failed
      setResult({
        success: false,
        error: `OAuth failed: ${error}`,
      });
      setIsProcessing(false);

      if (window.opener) {
        window.opener.postMessage(
          {
            type: "SLACK_OAUTH_ERROR",
            error: `OAuth failed: ${error}`,
          },
          window.location.origin
        );

        setTimeout(() => {
          window.close();
        }, 2000);
      }
      return;
    }

    if (code && state) {
      // Complete the OAuth process
      completeOAuth({
        variables: { code, state },
      });
    } else {
      setResult({
        success: false,
        error: "Missing OAuth parameters",
      });
      setIsProcessing(false);

      if (window.opener) {
        window.opener.postMessage(
          {
            type: "SLACK_OAUTH_ERROR",
            error: "Missing OAuth parameters",
          },
          window.location.origin
        );

        setTimeout(() => {
          window.close();
        }, 2000);
      }
    }
  }, [searchParams, completeOAuth]);

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <div className="bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10">
          <div className="text-center">
            {isProcessing ? (
              <>
                <ActivQMSLoader size="lg" className="mx-auto mb-4" />
                <h2 className="text-lg font-medium text-gray-900 mb-2">
                  Connecting to Slack...
                </h2>
                <p className="text-sm text-gray-600">
                  Please wait while we complete your Slack integration.
                </p>
              </>
            ) : result?.success ? (
              <>
                <CheckCircleIcon className="mx-auto h-12 w-12 text-green-500 mb-4" />
                <h2 className="text-lg font-medium text-gray-900 mb-2">
                  Successfully Connected!
                </h2>
                <p className="text-sm text-gray-600 mb-4">
                  Your Slack workspace "{result.teamName}" has been connected.
                  {result.channelName && (
                    <>
                      <br />
                      Messages will be sent to #{result.channelName}.
                    </>
                  )}
                </p>
                <p className="text-xs text-gray-500">
                  This window will close automatically...
                </p>
              </>
            ) : (
              <>
                <XCircleIcon className="mx-auto h-12 w-12 text-red-500 mb-4" />
                <h2 className="text-lg font-medium text-gray-900 mb-2">
                  Connection Failed
                </h2>
                <p className="text-sm text-gray-600 mb-4">
                  {result?.error || "An unknown error occurred"}
                </p>
                <p className="text-xs text-gray-500">
                  This window will close automatically...
                </p>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
